import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  EditOutlined as EditOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
  ViewSidebar as ViewSidebarIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import {
  PersonalFinancesOmhe,
  buildOmheTableCsv,
  OmheTransaction,
} from './PersonalFinancesOmhe';
import {
  CURRENT_YEAR,
  applyHouseholdUrlLabels,
  householdCategoryFromChartSelection,
  householdChartSelectionFromFilters,
  householdUrlFiltersFromChart,
  householdUrlGroup,
  householdUrlLabelsFromCatalog,
  isHouseholdMultiSelectEvent,
  readPersonalUrlFilters,
  toggleHouseholdChartSelection,
  writePersonalUrlFilters,
  allowedPersonalTabs,
  initialPersonalTab,
  type PersonalTab,
} from './personalFinancesUrl';
import {
  buildStatementRanks,
  compareLedgerDateSort,
  formatDayMonthYear,
  formatPostedDateTime,
  parseAibTransactionClock,
  type LedgerSortRow,
} from '../utils/personalStatementOrder';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitChipSx,
  FitCellText,
  fitTableTooltipProps,
} from '../utils/fitTable';
import { accountTagChipSx } from '../utils/accountTagChip';
import { cardLast4, displayCardLast4 } from '../utils/personalCardLast4';
import {
  cashflowOfCategory,
  cashflowOfRow,
  displayCashflowLabel,
  expenseGroupOfRow,
  expenseSignedAmount,
  householdRowsSimilarTo,
  householdSortHeaderSx,
  householdOffsetChipColor,
  householdOffsetChipVariant,
  isInvestmentCashflow,
  isNettingCashflow,
  isInvestmentPropertyName,
  matchesHouseholdCategoryFilter,
  rowMatchesHouseholdChartBars,
  groupInvestmentPropertyChartRows,
  openDetailRowSx,
  pairedOffsetIds,
  similarSearchNeedle,
  CHART_INVESTMENTS_FINANCE,
  CHART_INVESTMENTS_PROPERTY,
  CHART_MORTGAGE_LUMP,
  CASHFLOW_FILTER_OPTIONS,
} from '../utils/personalFinancesHousehold';
import { HouseholdSimilarTxDialog } from '../components/HouseholdSimilarTxDialog';
import { HouseholdDetailLine as DetailLine } from '../components/HouseholdDetailLine';
import { HouseholdFilterChip } from '../components/HouseholdFilterChip';
import { HouseholdFilterField } from '../components/HouseholdFilterField';
import { getHttpErrorMessageForUser, isToastOnlyApiErrorMessage } from '../utils/httpClientErrorMessage';
import { HouseholdTxCommentButton } from '../components/HouseholdTxCommentButton';
import { commentCsvValue, commentSearchText, HouseholdTxCommentThread, type PersonalTxComment } from '../components/HouseholdTxCommentThread';
import { cashflowChipSx, householdChartBarOpacity, householdChartRowFill } from '../utils/cashflowColors';
import {
  addYearAmount,
  chartAxisEuroTick,
  chartYearKeys,
  chartYearTotals,
  flattenYearStack,
  householdChartClickName,
  HOUSEHOLD_CHART_BAR_BACKGROUND,
  HOUSEHOLD_CHART_HOVER_CURSOR,
  mergeYearAmounts,
  postedYear,
  rowYearShadeFill,
  householdXAxisFromValues,
  yearLegendFill,
  yearLegendText,
} from '../utils/personalFinancesChart';
import { PersonalFinancesChartTooltip } from '../utils/personalFinancesChartTooltip';
import {
  HOUSEHOLD_CHART_ROW_HEIGHT,
  HOUSEHOLD_CHART_Y_AXIS_WIDTH,
  householdBarCountLabel,
  householdChartBarLabelText,
  householdChartYAxisTick,
} from '../utils/personalFinancesChartLabels';

const SECO_TABLE_COLS = buildFitColgroup([
  { key: 'select', pct: 3 },
  { key: 'id', pct: 4 },
  { key: 'date', pct: 6 },
  { key: 'cashflow', pct: 5 },
  { key: 'group', pct: 7 },
  { key: 'category', pct: 10 },
  { key: 'allocation', pct: 6 },
  { key: 'type', pct: 6 },
  { key: 'description', pct: 20 },
  { key: 'debit', pct: 6 },
  { key: 'credit', pct: 6 },
  { key: 'balance', pct: 6 },
  { key: 'card', pct: 3 },
  { key: 'account', pct: 4 },
  { key: 'actions', pct: 8 },
]);

interface SecoTransaction {
  id: number;
  posted_account: string;
  posted_date: string | null;
  description1: string;
  description2: string;
  description3: string;
  debit_amount: number | null;
  credit_amount: number | null;
  balance: number | null;
  posted_currency: string;
  transaction_type: string;
  proposed_category: string | null;
  expense_kind: string | null;
  category_rationale: string | null;
  offset_of_id: number | null;
  user_comment?: string | null;
  comments?: PersonalTxComment[] | null;
}

interface CategoryTotal {
  category: string;
  cashflow: string;
  count: number;
  debit: number;
  credit: number;
  byYear: Record<string, number>;
  countByYear: Record<string, number>;
  creditByYear: Record<string, number>;
  debitByYear: Record<string, number>;
  creditCountByYear: Record<string, number>;
  debitCountByYear: Record<string, number>;
}

interface KindTotal {
  count: number;
  debit: number;
  credit: number;
}

interface AccountTotal {
  account: string;
  ending: string;
  count: number;
  debit: number;
  credit: number;
}

interface TypeTotal {
  transaction_type: string;
  count: number;
  debit: number;
  credit: number;
}

interface SecoStats {
  total_transactions: number;
  min_date: string | null;
  max_date: string | null;
  total_debit: number;
  total_credit: number;
  latest_balance: number | null;
  transaction_types: string[];
  categories?: string[];
  category_totals?: CategoryTotal[];
  type_totals?: TypeTotal[];
  kind_totals?: Record<string, KindTotal>;
  account_totals?: AccountTotal[];
  classified_count?: number;
  years?: number[];
}

const money = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('en-IE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const yearMapCount = (map?: Record<string, number>) =>
  Object.values(map || {}).reduce((sum, amount) => sum + Number(amount || 0), 0);

const optionSuffix = (count?: number, debit?: number, credit?: number) => {
  const amount = (debit || 0) > 0 ? debit : credit;
  const parts: string[] = [];
  if (count) parts.push(String(count));
  if (amount) parts.push(`€${Math.round(amount).toLocaleString('en-IE')}`);
  if (!parts.length) return '';
  return ` (${parts.join(', ')})`;
};

const optionLabel = (label: string, debit?: number, credit?: number, count?: number) =>
  `${label}${optionSuffix(count, debit, credit)}`;

const secoClock = (row: Pick<SecoTransaction, 'description3'>) =>
  parseAibTransactionClock(row.description3);

const secoLedgerRow = (row: SecoTransaction): LedgerSortRow => ({
  id: row.id,
  posted_account: row.posted_account,
  posted_date: row.posted_date,
  debit_amount: row.debit_amount,
  credit_amount: row.credit_amount,
  balance: row.balance,
  clock: secoClock(row),
});

const formatSecoWhen = (row: Pick<SecoTransaction, 'posted_date' | 'description3'>) =>
  formatPostedDateTime(row.posted_date, secoClock(row));

const descriptionOf = (row: SecoTransaction) =>
  [
    row.description1,
    row.description2,
    secoClock(row) ? '' : row.description3,
  ]
    .filter(Boolean)
    .join(' · ');

const allocationOf = (kind: string | null | undefined) => {
  if (kind === 'personal') return 'Personal';
  if (kind === 'business' || kind === 'possible_stl_business') return 'Business';
  return 'N/A';
};

const allocationColor = (kind: string | null | undefined): 'secondary' | 'default' =>
  allocationOf(kind) === 'Business' ? 'secondary' : 'default';

const accountChip = (account: string | null | undefined, ending?: string | null) => {
  const raw = account || '';
  if (raw.includes('****')) {
    const card = raw.replace(/\D/g, '').slice(-4);
    return card ? `CC …${card}` : raw;
  }
  const digits = (ending || '').trim() || raw.replace(/\D/g, '').slice(-3);
  return digits ? `…${digits}` : raw || '—';
};

const DETAIL_PANEL_WIDTH_PX = 440;

const SecoOffsetSideCard: React.FC<{
  row: SecoTransaction;
  role: 'this' | 'other';
  offsetLabel: string;
  onOpen?: () => void;
}> = ({ row, role, offsetLabel, onOpen }) => {
  const heading = role === 'this' ? 'This side' : 'Other side';
  const amount = row.debit_amount
    ? `Debit ${money(row.debit_amount)}`
    : row.credit_amount
      ? `Credit ${money(row.credit_amount)}`
      : '—';
  return (
    <Paper
      variant="outlined"
      data-testid={`seco-detail-side-${role}`}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `View TX ${row.id}` : undefined}
      sx={{
        p: 1.5,
        mb: 1.5,
        cursor: onOpen ? 'pointer' : 'default',
        borderColor: role === 'this' ? 'primary.light' : 'divider',
        bgcolor: role === 'this' ? 'action.hover' : 'background.paper',
        '&:hover': onOpen ? { borderColor: 'primary.main' } : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.75 }}>
        <Typography variant="subtitle2">
          {heading} · TX {row.id}
        </Typography>
        {offsetLabel ? (
          <Chip
            size="small"
            label={offsetLabel}
            color={householdOffsetChipColor(offsetLabel)}
            variant={householdOffsetChipVariant(offsetLabel)}
          />
        ) : null}
      </Stack>
      <Typography variant="body2">
        {formatSecoWhen(row)} · {accountChip(row.posted_account)} · {amount}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {displayCategory(row.proposed_category)} · {descriptionOf(row) || '—'}
      </Typography>
      {onOpen ? (
        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.75 }}>
          View this row
        </Typography>
      ) : null}
    </Paper>
  );
};

const ALLOCATION_OPTIONS: { value: string; label: string }[] = [
  { value: 'personal', label: 'Personal' },
  { value: 'business', label: 'Business' },
  { value: 'n/a', label: 'N/A' },
];

// Parent buckets for the chart. Keep in sync with backend CATEGORY_GROUPS.
const CATEGORY_GROUPS: Record<string, string> = {
  Groceries: 'Living',
  Dining: 'Living',
  Cafe: 'Living',
  'Health & gym': 'Living',
  Pharmacy: 'Living',
  Medical: 'Living',
  'Medical (Natural)': 'Living',
  Charity: 'Living',
  Gifts: 'Living',
  'Charity & church': 'Living',
  'Household & shopping': 'Living',
  Clothing: 'Living',
  Computer: 'Living',
  Digital: 'Living',
  Cleaning: 'Living',
  'Transport & fuel': 'Living',
  Transport: 'Living',
  'Car Rental': 'Living',
  'Cash & ATM': 'Living',
  Broadband: 'Utilities & comms',
  'Mobile phone': 'Utilities & comms',
  'TV & streaming': 'Utilities & comms',
  'TV licence': 'Utilities & comms',
  Electricity: 'Utilities & comms',
  Gas: 'Utilities & comms',
  Refuse: 'Utilities & comms',
  Water: 'Utilities & comms',
  'Other utilities': 'Utilities & comms',
  Insurance: 'Insurance',
  'Gadget Insurance': 'Insurance',
  'Car Insurance': 'Insurance',
  'Mortgage & loans': 'Housing & finance',
  Loan: 'Housing & finance',
  'Loan repayment': 'Housing & finance',
  LPT: 'Housing & finance',
  'Income Tax': 'Housing & finance',
  'Bank fees': 'Finance',
  'Credit Card Payment': 'Internal Transfers',
  'Credit card repayment': 'Internal Transfers',
  'Transfers to savings': 'Internal Transfers',
  'Transfers to business': 'Internal Transfers',
  'Family support': 'Internal Transfers',
  'Internal Transfer': 'Internal Transfers',
  Income: 'Income',
  'Income (PAYE)': 'Income',
  'Income (RRLtd)': 'Income',
  'Income (Other)': 'Income',
  'State Pension': 'Income',
  'State Pension B': 'Income',
  Refunds: 'Refunds',
  Reverted: 'Reverted',
  '3GD Mortgage lump-sum': '3GD Mortgage lump-sum',
  'Tax-free Gift': 'Income',
  Unknown: 'Unknown',
};

const CATEGORY_EDIT_OPTIONS = Object.keys(CATEGORY_GROUPS).filter(
  (name) => name !== 'Charity & church' && name !== 'Credit card repayment'
);

const secoUrlLabels = () => householdUrlLabelsFromCatalog(CATEGORY_GROUPS);
const readSecoUrlFilters = () => applyHouseholdUrlLabels(readPersonalUrlFilters(), secoUrlLabels());

const CHART_INCOME = 'Income';
const CHART_EXPENSES = 'Expenses';
const CHART_TRANSFERS = 'Internal Transfers';
const CHART_UNKNOWN = 'Unknown';
const CHART_NA = 'N/A';

const displayCategory = (category: string | null | undefined) => {
  const text = (category || '').trim();
  if (!text || text === 'Uncategorised') return CHART_UNKNOWN;
  if (text === 'Credit card repayment') return 'Credit Card Payment';
  return text;
};

const groupName = (category: string | null | undefined) =>
  CATEGORY_GROUPS[displayCategory(category)] || CHART_UNKNOWN;

const isIncomeCategory = (category: string) => groupName(category) === CHART_INCOME;
const isTransferCategory = (category: string) => groupName(category) === CHART_TRANSFERS;
const isInvestmentCategory = (category: string) =>
  isInvestmentCashflow(cashflowOfCategory(category, CATEGORY_GROUPS));
const isMortgageLumpCategory = (category: string) =>
  cashflowOfCategory(category, CATEGORY_GROUPS) === CHART_MORTGAGE_LUMP;
const isUnknownCategory = (category: string) => groupName(category) === CHART_UNKNOWN;
const isExpenseCategory = (category: string) =>
  !isIncomeCategory(category) &&
  !isTransferCategory(category) &&
  !isInvestmentCategory(category) &&
  !isMortgageLumpCategory(category) &&
  !isUnknownCategory(category);

const expenseGroupOf = (category: string | null | undefined) => {
  const cat = displayCategory(category);
  return isExpenseCategory(cat) ? groupName(cat) : '';
};

const cashflowOf = (category: string | null | undefined) => {
  const cat = displayCategory(category);
  if (isIncomeCategory(cat)) return CHART_INCOME;
  if (isTransferCategory(cat)) return CHART_TRANSFERS;
  if (isInvestmentCategory(cat)) return cashflowOfCategory(cat, CATEGORY_GROUPS);
  if (isMortgageLumpCategory(cat)) return CHART_MORTGAGE_LUMP;
  if (isExpenseCategory(cat)) return CHART_EXPENSES;
  return CHART_UNKNOWN;
};

const secoCashflowOf = (row: SecoTransaction, pairedIds: Set<number>) =>
  cashflowOfRow(row, CATEGORY_GROUPS, pairedIds);

const secoExpenseGroupOf = (row: SecoTransaction, pairedIds: Set<number>) =>
  expenseGroupOfRow(row, CATEGORY_GROUPS, pairedIds);

const cashflowColor = (
  side: string
): 'error' | 'info' | 'default' => {
  if (side === CHART_EXPENSES) return 'error';
  if (side === CHART_TRANSFERS) return 'info';
  return 'default';
};

const offsetChipColor = (label: string): 'error' | 'info' | 'default' => {
  if (label === 'Refunded' || label.startsWith('Offsets ')) return 'error';
  if (label === 'Linked' || label.startsWith('Linked ') || label.startsWith('Repays ') || label === 'Repaid')
    return 'info';
  return 'default';
};

const offsetChipVariant = (label: string): 'filled' | 'outlined' =>
  label === 'Refunded' ? 'filled' : 'outlined';

const csvEscape = (value: string) =>
  /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const csvAmount = (value: number | null | undefined) =>
  value == null ? '' : Number(value).toFixed(2);

export const SECO_CSV_HEADERS = [
  'ID',
  'Date',
  'Cashflow',
  'Group',
  'Category',
  'Allocation',
  'Type',
  'Description',
  'Debit',
  'Credit',
  'Balance',
  'Card',
  'Account',
  'Comment',
] as const;

export const buildSecoTableCsv = (rows: SecoTransaction[]) => {
  const pairedIds = pairedOffsetIds(rows);
  const lines = [
    SECO_CSV_HEADERS.join(','),
    ...rows.map((row) =>
      [
        String(row.id),
        formatSecoWhen(row),
        secoCashflowOf(row, pairedIds),
        secoExpenseGroupOf(row, pairedIds) || '',
        displayCategory(row.proposed_category),
        allocationOf(row.expense_kind),
        row.transaction_type || '',
        descriptionOf(row),
        csvAmount(row.debit_amount),
        csvAmount(row.credit_amount),
        csvAmount(row.balance),
        cardLast4(row.posted_account),
        row.posted_account || '',
        commentCsvValue(row),
      ]
        .map(csvEscape)
        .join(',')
    ),
  ];
  return `${lines.join('\n')}\n`;
};

const downloadCsv = (filename: string, csv: string) => {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

type PageSize = 10 | 25 | 50 | 100 | 200 | 1000 | 'all';
const PAGE_SIZE_OPTIONS: PageSize[] = [10, 25, 50, 100, 200, 1000, 'all'];

type SecoSortKey =
  | 'id'
  | 'posted_date'
  | 'cashflow'
  | 'category_group'
  | 'proposed_category'
  | 'allocation'
  | 'transaction_type'
  | 'description'
  | 'debit_amount'
  | 'credit_amount'
  | 'balance'
  | 'card_last4'
  | 'posted_account';

const TEXT_SORT_KEYS = new Set<SecoSortKey>([
  'cashflow',
  'category_group',
  'proposed_category',
  'allocation',
  'transaction_type',
  'description',
  'posted_account',
  'card_last4',
]);

const categoryTotalsFromRows = (rows: SecoTransaction[]): CategoryTotal[] => {
  const pairedIds = pairedOffsetIds(rows);
  const map = new Map<string, CategoryTotal>();
  rows.forEach((row) => {
    const category = displayCategory(row.proposed_category);
    const cashflow = secoCashflowOf(row, pairedIds);
    const key = `${cashflow}::${category}`;
    const current = map.get(key) || {
      category,
      cashflow,
      count: 0,
      debit: 0,
      credit: 0,
      byYear: {},
      countByYear: {},
      creditByYear: {},
      debitByYear: {},
      creditCountByYear: {},
      debitCountByYear: {},
    };
    current.count += 1;
    current.debit += Number(row.debit_amount || 0);
    current.credit += Number(row.credit_amount || 0);
    const amount =
      cashflow === CHART_INCOME
        ? Number(row.credit_amount || 0)
        : cashflow === CHART_EXPENSES
          ? expenseSignedAmount(row)
          : cashflow === CHART_MORTGAGE_LUMP
            ? Number(row.credit_amount || 0) - Number(row.debit_amount || 0)
            : Number(row.debit_amount || 0) + Number(row.credit_amount || 0);
    const year = postedYear(row.posted_date);
    addYearAmount(current.byYear, year, amount);
    addYearAmount(current.countByYear, year, 1);
    addYearAmount(current.creditByYear, year, Number(row.credit_amount || 0));
    addYearAmount(current.debitByYear, year, Number(row.debit_amount || 0));
    addYearAmount(current.creditCountByYear, year, Number(row.credit_amount || 0) > 0 ? 1 : 0);
    addYearAmount(current.debitCountByYear, year, Number(row.debit_amount || 0) > 0 ? 1 : 0);
    map.set(key, current);
  });
  return Array.from(map.values()).sort((a, b) => b.debit - a.debit);
};

const rowMatchesTableFilters = (
  row: SecoTransaction,
  filters: {
    search: string;
    transactionType: string;
    category: string;
    chartGroup: string;
    chartSide: string;
    selectedChartNames: string[];
    expenseKind: string;
  },
  pairedIds: Set<number>
) => {
  if (filters.search.trim()) {
    const needle = filters.search.trim().toLowerCase();
    const hay = [
      String(row.id),
      row.offset_of_id ? String(row.offset_of_id) : '',
      row.description1,
      row.description2,
      row.description3,
      row.posted_account,
      cardLast4(row.posted_account),
      commentSearchText(row),
    ]
      .join(' ')
      .toLowerCase();
    if (!hay.includes(needle)) return false;
  }
  if (filters.transactionType && row.transaction_type !== filters.transactionType) return false;
  if (filters.expenseKind) {
    const wanted =
      filters.expenseKind === 'n/a'
        ? 'N/A'
        : filters.expenseKind === 'business'
          ? 'Business'
          : 'Personal';
    if (allocationOf(row.expense_kind) !== wanted) return false;
  }
  const side = secoCashflowOf(row, pairedIds);
  const selected = filters.selectedChartNames.filter(Boolean);
  if (selected.length) {
    return rowMatchesHouseholdChartBars({
      selected,
      chartSide: filters.chartSide,
      chartGroup: filters.chartGroup,
      cashflow: side,
      group: secoExpenseGroupOf(row, pairedIds),
      category: displayCategory(row.proposed_category),
      groups: CATEGORY_GROUPS,
    });
  }
  if (filters.category) {
    if (!matchesHouseholdCategoryFilter(row.proposed_category, filters.category, CATEGORY_GROUPS)) return false;
    if (filters.chartSide && side !== filters.chartSide) return false;
  } else if (filters.chartSide === CHART_EXPENSES && filters.chartGroup) {
    if (secoExpenseGroupOf(row, pairedIds) !== filters.chartGroup || side !== CHART_EXPENSES)
      return false;
  } else if (filters.chartSide && side !== filters.chartSide) {
    return false;
  }
  return true;
};

const sortValue = (
  row: SecoTransaction,
  column: SecoSortKey,
  pairedIds: Set<number>
): string | number | null => {
  switch (column) {
    case 'id':
      return row.id;
    case 'posted_date':
      return row.posted_date;
    case 'cashflow':
      return secoCashflowOf(row, pairedIds);
    case 'category_group':
      return secoExpenseGroupOf(row, pairedIds) || '';
    case 'proposed_category':
      return displayCategory(row.proposed_category);
    case 'allocation':
      return allocationOf(row.expense_kind);
    case 'transaction_type':
      return row.transaction_type;
    case 'description':
      return descriptionOf(row);
    case 'debit_amount':
      return row.debit_amount;
    case 'credit_amount':
      return row.credit_amount;
    case 'balance':
      return row.balance;
    case 'posted_account':
      return row.posted_account;
    case 'card_last4':
      return cardLast4(row.posted_account);
    default:
      return null;
  }
};

const compareSecoRows = (
  left: SecoTransaction,
  right: SecoTransaction,
  column: SecoSortKey,
  direction: 'asc' | 'desc',
  ranks: Map<number, number>,
  pairedIds: Set<number>
) => {
  if (column === 'posted_date') {
    return compareLedgerDateSort(secoLedgerRow(left), secoLedgerRow(right), direction, ranks);
  }
  const av = sortValue(left, column, pairedIds);
  const bv = sortValue(right, column, pairedIds);
  const aMissing = av == null || av === '';
  const bMissing = bv == null || bv === '';
  if (aMissing || bMissing) {
    if (!(aMissing && bMissing)) return aMissing ? 1 : -1;
  } else if (typeof av === 'number' && typeof bv === 'number') {
    if (av !== bv) return direction === 'asc' ? av - bv : bv - av;
  } else {
    const cmp = String(av).localeCompare(String(bv), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
  }
  if (column !== 'id' && left.id !== right.id) {
    return direction === 'asc' ? left.id - right.id : right.id - left.id;
  }
  return 0;
};

const SecoSortHeader: React.FC<{
  column: SecoSortKey;
  label: string;
  sortBy: SecoSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (column: SecoSortKey) => void;
}> = ({ column, label, sortBy, sortDir, onSort }) => (
  <TableCell
    align="center"
    sortDirection={sortBy === column ? sortDir : false}
    sx={householdSortHeaderSx}
  >
    <TableSortLabel
      active={sortBy === column}
      direction={sortBy === column ? sortDir : 'asc'}
      onClick={() => onSort(column)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

const SecoPaginationBar: React.FC<{
  total: number;
  perPage: PageSize;
  pages: number;
  page: number;
  placement: 'top' | 'bottom';
  onPerPage: (size: PageSize) => void;
  onPage: (next: number) => void;
  search?: React.ReactNode;
}> = ({ total, perPage, pages, page, placement, onPerPage, onPage, search }) => (
  <Stack
    direction="row"
    justifyContent="flex-start"
    alignItems="center"
    flexWrap="wrap"
    useFlexGap
    spacing={2}
    data-testid={`seco-pagination-${placement}`}
    sx={{ width: 1, ...(placement === 'top' ? { mb: 2 } : { mt: 2 }) }}
  >
    <Typography variant="body2" color="text.secondary">
      {total} rows
    </Typography>
    <FormControl size="small" sx={{ minWidth: 120 }} data-testid={`seco-page-size-${placement}`}>
      <InputLabel>Rows</InputLabel>
      <Select
        label="Rows"
        value={String(perPage)}
        onChange={(event) => {
          const next = String(event.target.value);
          onPerPage(next === 'all' ? 'all' : (Number(next) as PageSize));
        }}
      >
        {PAGE_SIZE_OPTIONS.map((size) => (
          <MenuItem key={size} value={String(size)}>
            {size === 'all' ? 'All' : size}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <Pagination
      count={pages}
      page={page}
      onChange={(_, next) => onPage(next)}
      color="primary"
    />
    {search ? <Box sx={{ ml: 'auto' }}>{search}</Box> : null}
  </Stack>
);

interface ChartRow {
  name: string;
  value: number;
  count: number;
  level: 'side' | 'group' | 'category';
  byYear: Record<string, number>;
  countByYear: Record<string, number>;
  incomeExpenseSplit?: boolean;
  credit?: number;
  debit?: number;
  creditCount?: number;
  debitCount?: number;
  creditByYear?: Record<string, number>;
  debitByYear?: Record<string, number>;
  creditCountByYear?: Record<string, number>;
  debitCountByYear?: Record<string, number>;
}

export const PersonalFinances: React.FC = () => {
  const { user } = useAuth();
  const initialUrl = readSecoUrlFilters();
  const initialSelection = householdChartSelectionFromFilters(initialUrl, secoUrlLabels());
  const allowedTabs = useMemo(() => allowedPersonalTabs(user), [user]);
  const [tab, setTab] = useState<PersonalTab>(() => initialPersonalTab(initialUrl.tab, user));
  const canImport = user?.role === 'admin' || user?.role === 'owner';

  const [allRows, setAllRows] = useState<SecoTransaction[]>([]);
  const [stats, setStats] = useState<SecoStats | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<PageSize>(200);
  const [sortBy, setSortBy] = useState<SecoSortKey>('posted_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [searchInput, setSearchInput] = useState(initialUrl.q);
  const [search, setSearch] = useState(initialUrl.q);
  const [transactionType, setTransactionType] = useState(initialUrl.type);
  const [category, setCategory] = useState(householdCategoryFromChartSelection(initialSelection));
  const [expenseKind, setExpenseKind] = useState(initialUrl.allocation);
  const [accountEnding, setAccountEnding] = useState(initialUrl.ending);
  const [year, setYear] = useState(initialUrl.year);
  const [chartOpen, setChartOpen] = useState(true);
  const [chartSide, setChartSide] = useState(initialSelection.side);
  const [chartGroup, setChartGroup] = useState(initialSelection.group);
  const [selectedChartNames, setSelectedChartNames] = useState<string[]>(initialSelection.selected);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [detailRowId, setDetailRowId] = useState<number | null>(null);
  const [commentFocus, setCommentFocus] = useState(false);
  const [similarRowId, setSimilarRowId] = useState<number | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [omheExportRows, setOmheExportRows] = useState<OmheTransaction[]>([]);
  const [omheReloadToken, setOmheReloadToken] = useState(0);
  const secoLoadSeq = useRef(0);

  useEffect(() => {
    const urlChart = householdUrlFiltersFromChart({
      side: chartSide,
      group: chartGroup,
      selected: selectedChartNames,
      catalog: CATEGORY_GROUPS,
    });
    writePersonalUrlFilters({
      ...readPersonalUrlFilters(),
      tab,
      year,
      ...(tab === 'seco'
        ? {
            q: search,
            type: transactionType,
            ending: accountEnding,
            side: urlChart.side,
            group: urlChart.group,
            category: urlChart.category,
            allocation: expenseKind,
          }
        : {}),
    });
  }, [
    tab,
    year,
    search,
    transactionType,
    accountEnding,
    chartSide,
    chartGroup,
    selectedChartNames,
    expenseKind,
  ]);

  useEffect(() => {
    if (!allowedTabs.includes(tab)) {
      setTab(allowedTabs[0]);
    }
  }, [allowedTabs, tab]);

  useEffect(() => {
    const applyUrl = () => {
      const next = readSecoUrlFilters();
      const selection = householdChartSelectionFromFilters(next, secoUrlLabels());
      setTab(initialPersonalTab(next.tab, user));
      setYear(next.year);
      setSearchInput(next.q);
      setSearch(next.q);
      setTransactionType(next.type);
      setAccountEnding(next.ending);
      setChartSide(selection.side);
      setChartGroup(selection.group);
      setSelectedChartNames(selection.selected);
      setCategory(householdCategoryFromChartSelection(selection));
      setExpenseKind(next.allocation);
      setPage(1);
    };
    window.addEventListener('popstate', applyUrl);
    return () => window.removeEventListener('popstate', applyUrl);
  }, [user]);

  const handleOmheRowsChange = useCallback((rows: OmheTransaction[]) => {
    setOmheExportRows(rows);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadSeco = useCallback(async () => {
    const seq = (secoLoadSeq.current += 1);
    setLoading(true);
    setAllRows([]);
    setError('');
    try {
      const params = new URLSearchParams({
        page: '1',
        per_page: 'all',
      });
      if (accountEnding) params.set('account_ending', accountEnding);
      if (year) params.set('year', year);
      const statsParams = new URLSearchParams();
      if (accountEnding) statsParams.set('account_ending', accountEnding);
      if (year) statsParams.set('year', year);
      const statsQuery = statsParams.toString();
      const [listRes, statsRes] = await Promise.all([
        api.get(`/api/personal-finances/seco?${params.toString()}`),
        api.get(statsQuery ? `/api/personal-finances/seco/stats?${statsQuery}` : '/api/personal-finances/seco/stats'),
      ]);
      if (seq !== secoLoadSeq.current) return;
      setAllRows(listRes.data.transactions || []);
      setStats(statsRes.data);
    } catch (err: any) {
      if (seq !== secoLoadSeq.current) return;
      setError(getHttpErrorMessageForUser(err, 'Failed to load SeCo personal finances'));
    } finally {
      if (seq === secoLoadSeq.current) setLoading(false);
    }
  }, [accountEnding, year]);

  useEffect(() => {
    if (tab === 'seco') {
      loadSeco();
    }
  }, [tab, loadSeco]);

  const statementRanks = useMemo(
    () => buildStatementRanks(allRows.map(secoLedgerRow)),
    [allRows]
  );
  const secoPairedIds = useMemo(() => pairedOffsetIds(allRows), [allRows]);
  const filteredRows = useMemo(() => {
    const next = allRows.filter((row) =>
      rowMatchesTableFilters(
        row,
        {
          search,
          transactionType,
          category,
          chartGroup,
          chartSide,
          selectedChartNames,
          expenseKind,
        },
        secoPairedIds
      )
    );
    next.sort((left, right) =>
      compareSecoRows(left, right, sortBy, sortDir, statementRanks, secoPairedIds)
    );
    return next;
  }, [
    allRows,
    search,
    transactionType,
    category,
    chartGroup,
    chartSide,
    selectedChartNames,
    expenseKind,
    sortBy,
    sortDir,
    statementRanks,
    secoPairedIds,
  ]);
  const total = filteredRows.length;
  const pages = perPage === 'all' ? 1 : Math.max(1, Math.ceil(total / perPage));
  const currentPage = Math.min(page, pages);
  const visibleRows = useMemo(() => {
    if (perPage === 'all') return filteredRows;
    const start = (currentPage - 1) * perPage;
    return filteredRows.slice(start, start + perPage);
  }, [filteredRows, perPage, currentPage]);

  const pageIds = useMemo(() => visibleRows.map((row) => row.id), [visibleRows]);
  const offsetTargetIds = useMemo(() => {
    const ids = new Set<number>();
    allRows.forEach((row) => {
      if (row.offset_of_id) ids.add(row.offset_of_id);
    });
    return ids;
  }, [allRows]);
  const rowsById = useMemo(() => {
    const map = new Map<number, SecoTransaction>();
    allRows.forEach((row) => map.set(row.id, row));
    return map;
  }, [allRows]);
  const offsetLinkerByTargetId = useMemo(() => {
    const map = new Map<number, SecoTransaction>();
    allRows.forEach((row) => {
      if (row.offset_of_id) map.set(row.offset_of_id, row);
    });
    return map;
  }, [allRows]);
  const offsetChip = (row: SecoTransaction) => {
    const other = row.offset_of_id
      ? rowsById.get(row.offset_of_id)
      : offsetLinkerByTargetId.get(row.id);
    const crossAccount = Boolean(
      other && accountChip(other.posted_account) !== accountChip(row.posted_account)
    );
    const loanLink = Boolean(
      other &&
        [row, other].some((item) =>
          `${item.description1} ${item.proposed_category || ''}`.toUpperCase().includes('LOAN')
        )
    );
    if (row.offset_of_id) {
      if (loanLink) return `Repays ${row.offset_of_id}`;
      return crossAccount ? `Linked ${row.offset_of_id}` : `Offsets ${row.offset_of_id}`;
    }
    if (offsetTargetIds.has(row.id)) {
      if (loanLink) return 'Repaid';
      return crossAccount ? 'Linked' : 'Refunded';
    }
    return '';
  };
  const offsetCounterpart = (row: SecoTransaction) =>
    row.offset_of_id ? rowsById.get(row.offset_of_id) || null : offsetLinkerByTargetId.get(row.id) || null;
  const detailRow = useMemo(
    () => (detailRowId == null ? null : allRows.find((row) => row.id === detailRowId) || null),
    [allRows, detailRowId]
  );
  const similarAnchor = useMemo(
    () => (similarRowId == null ? null : allRows.find((row) => row.id === similarRowId) || null),
    [allRows, similarRowId]
  );
  const similarTxViews = useMemo(() => {
    if (!similarAnchor) return [];
    return householdRowsSimilarTo(allRows, similarAnchor, (row) => row.description1).map((row) => ({
      id: row.id,
      when: formatSecoWhen(row),
      description: descriptionOf(row),
      debit: money(row.debit_amount),
      credit: money(row.credit_amount),
      category: displayCategory(row.proposed_category),
      isAnchor: row.id === similarAnchor.id,
    }));
  }, [allRows, similarAnchor]);
  const detailCounterpart = detailRow ? offsetCounterpart(detailRow) : null;
  const openDetailRow = (id: number, options?: { focusComment?: boolean }) => {
    setCommentFocus(Boolean(options?.focusComment));
    setDetailRowId(id);
    if (perPage === 'all') return;
    const index = filteredRows.findIndex((row) => row.id === id);
    if (index >= 0) setPage(Math.floor(index / perPage) + 1);
  };
  const closeDetailRow = () => {
    setCommentFocus(false);
    setDetailRowId(null);
  };
  useEffect(() => {
    if (detailRowId == null) return;
    const el = document.querySelector(`[data-testid="seco-row-${detailRowId}"]`);
    if (el instanceof HTMLElement && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [detailRowId, currentPage]);
  const selectedOnPage = pageIds.filter((id) => selectedIds.has(id));
  const allPageSelected = pageIds.length > 0 && selectedOnPage.length === pageIds.length;
  const somePageSelected = selectedOnPage.length > 0 && !allPageSelected;

  const handleSort = (column: SecoSortKey) => {
    setPage(1);
    if (sortBy === column) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    setSortDir(TEXT_SORT_KEYS.has(column) ? 'asc' : 'desc');
  };

  const toggleRowSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const reloadStats = async () => {
    const statsParams = new URLSearchParams();
    if (accountEnding) statsParams.set('account_ending', accountEnding);
    if (year) statsParams.set('year', year);
    const statsQuery = statsParams.toString();
    const statsRes = await api.get(
      statsQuery ? `/api/personal-finances/seco/stats?${statsQuery}` : '/api/personal-finances/seco/stats'
    );
    setStats(statsRes.data);
  };

  const applySecoTransaction = (updated: SecoTransaction) => {
    setAllRows((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
  };

  const saveCategory = async (rowId: number, nextCategory: string) => {
    const current = allRows.find((row) => row.id === rowId);
    if (current && displayCategory(current.proposed_category) === nextCategory) {
      setEditingId(null);
      return;
    }
    setSavingId(rowId);
    setError('');
    try {
      const response = await api.patch(`/api/personal-finances/seco/${rowId}`, {
        proposed_category: nextCategory,
      });
      const updated = response.data.transaction as SecoTransaction;
      setAllRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, ...updated } : row)));
      setEditingId(null);
      await reloadStats();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update category');
    } finally {
      setSavingId(null);
    }
  };

  const applyBulkCategory = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length || !bulkCategory) return;
    setBulkSaving(true);
    setError('');
    try {
      const response = await api.post('/api/personal-finances/seco/bulk-category', {
        ids,
        proposed_category: bulkCategory,
      });
      const updatedRows = (response.data.transactions || []) as SecoTransaction[];
      const byId = new Map(updatedRows.map((row) => [row.id, row]));
      setAllRows((prev) => prev.map((row) => (byId.has(row.id) ? { ...row, ...byId.get(row.id) } : row)));
      setSelectedIds(new Set());
      setBulkCategory('');
      setEditingId(null);
      setNotice(`Updated ${response.data.updated || ids.length} rows to ${bulkCategory}.`);
      await reloadStats();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update selected categories');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, next: string) => {
    const nextTab: PersonalTab = next === 'omhe' ? 'omhe' : 'seco';
    if (!allowedTabs.includes(nextTab)) return;
    setTab(nextTab);
    setSelectedIds(new Set());
    setEditingId(null);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,text/csv';
    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const form = new FormData();
      form.append('file', file);
      const household = tab === 'omhe' ? 'omhe' : 'seco';
      try {
        setImporting(true);
        setError('');
        const response = await api.post(`/api/personal-finances/${household}/import`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: household === 'omhe' ? 120000 : 180000,
        });
        const result = response.data.result || {};
        if (household === 'omhe') {
          setNotice(
            `Imported ${result.created || 0} new OmHe rows (${result.skipped || 0} already saved).`
          );
          setOmheReloadToken((value) => value + 1);
        } else {
          const classified = result.classified || {};
          const classifiedNote = classified.error
            ? ` Classification skipped: ${classified.error}.`
            : ` Classified ${classified.updated || 0} rows.`;
          setNotice(
            `Imported ${result.created || 0} new SeCo rows (${result.skipped || 0} already saved).${classifiedNote}`
          );
          await loadSeco();
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Import failed');
      } finally {
        setImporting(false);
      }
    };
    input.click();
  };

  const handleExportCsv = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const yearPart = year || 'all-years';
    if (tab === 'omhe') {
      if (!omheExportRows.length) return;
      downloadCsv(`omhe-personal-${yearPart}-${stamp}.csv`, buildOmheTableCsv(omheExportRows));
      setNotice(`Exported ${omheExportRows.length} row${omheExportRows.length === 1 ? '' : 's'}.`);
      return;
    }
    if (!filteredRows.length) return;
    downloadCsv(`seco-personal-${yearPart}-${stamp}.csv`, buildSecoTableCsv(filteredRows));
    setNotice(`Exported ${filteredRows.length} row${filteredRows.length === 1 ? '' : 's'}.`);
  };

  const categoryTotals = useMemo(() => categoryTotalsFromRows(allRows), [allRows]);
  const expenseCategories = useMemo(
    () => categoryTotals.filter((row) => row.cashflow === CHART_EXPENSES),
    [categoryTotals]
  );
  const incomeCategories = useMemo(
    () =>
      categoryTotals.filter((row) => row.cashflow === CHART_INCOME && row.credit > 0),
    [categoryTotals]
  );
  const transferCategories = useMemo(
    () =>
      categoryTotals.filter(
        (row) =>
          row.cashflow === CHART_TRANSFERS && (row.debit > 0 || row.credit > 0)
      ),
    [categoryTotals]
  );
  const investmentPropertyCategories = useMemo(
    () =>
      categoryTotals.filter(
        (row) =>
          row.cashflow === CHART_INVESTMENTS_PROPERTY && (row.debit > 0 || row.credit > 0)
      ),
    [categoryTotals]
  );
  const investmentFinanceCategories = useMemo(
    () =>
      categoryTotals.filter(
        (row) =>
          row.cashflow === CHART_INVESTMENTS_FINANCE && (row.debit > 0 || row.credit > 0)
      ),
    [categoryTotals]
  );
  const mortgageLumpCategories = useMemo(
    () =>
      categoryTotals.filter(
        (row) =>
          row.cashflow === CHART_MORTGAGE_LUMP && (row.debit > 0 || row.credit > 0)
      ),
    [categoryTotals]
  );
  const unknownCategories = useMemo(
    () =>
      categoryTotals.filter((row) => row.cashflow === CHART_UNKNOWN),
    [categoryTotals]
  );
  const groupTotals = useMemo(() => {
    const map = new Map<string, ChartRow>();
    expenseCategories.forEach((row) => {
      const name = expenseGroupOf(row.category) || groupName(row.category);
      if (!name || name === CHART_INCOME || name === CHART_UNKNOWN) return;
      const current = map.get(name) || {
        name,
        value: 0,
        count: 0,
        level: 'group' as const,
        byYear: {},
        countByYear: {},
      };
      current.value += row.debit - row.credit;
      current.count += row.count;
      current.byYear = mergeYearAmounts(current.byYear, row.byYear);
      current.countByYear = mergeYearAmounts(current.countByYear, row.countByYear);
      map.set(name, current);
    });
    return Array.from(map.values())
      .filter((row) => row.count > 0)
      .sort((a, b) => b.value - a.value);
  }, [expenseCategories]);
  const sideTotals = useMemo<ChartRow[]>(() => {
    const blankSide = () => ({
      value: 0,
      count: 0,
      byYear: {} as Record<string, number>,
      countByYear: {} as Record<string, number>,
      credit: 0,
      debit: 0,
      creditCount: 0,
      debitCount: 0,
      creditByYear: {} as Record<string, number>,
      debitByYear: {} as Record<string, number>,
      creditCountByYear: {} as Record<string, number>,
      debitCountByYear: {} as Record<string, number>,
    });
    const income = incomeCategories.reduce(
      (acc, row) => ({
        ...acc,
        value: acc.value + row.credit,
        count: acc.count + row.count,
        byYear: mergeYearAmounts(acc.byYear, row.byYear),
        countByYear: mergeYearAmounts(acc.countByYear, row.countByYear),
      }),
      blankSide()
    );
    const expenses = expenseCategories.reduce(
      (acc, row) => ({
        ...acc,
        value: acc.value + row.debit - row.credit,
        count: acc.count + row.count,
        byYear: mergeYearAmounts(acc.byYear, row.byYear),
        countByYear: mergeYearAmounts(acc.countByYear, row.countByYear),
      }),
      blankSide()
    );
    const transfers = transferCategories.reduce(
      (acc, row) => ({
        ...acc,
        value: acc.value + row.debit + row.credit,
        count: acc.count + row.count,
        byYear: mergeYearAmounts(acc.byYear, row.byYear),
        countByYear: mergeYearAmounts(acc.countByYear, row.countByYear),
      }),
      blankSide()
    );
    const investmentProperty = investmentPropertyCategories.reduce(
      (acc, row) => ({
        ...acc,
        value: acc.value + row.debit + row.credit,
        count: acc.count + row.count,
        byYear: mergeYearAmounts(acc.byYear, row.byYear),
        countByYear: mergeYearAmounts(acc.countByYear, row.countByYear),
        credit: acc.credit + row.credit,
        debit: acc.debit + row.debit,
        creditCount: acc.creditCount + yearMapCount(row.creditCountByYear),
        debitCount: acc.debitCount + yearMapCount(row.debitCountByYear),
        creditByYear: mergeYearAmounts(acc.creditByYear, row.creditByYear),
        debitByYear: mergeYearAmounts(acc.debitByYear, row.debitByYear),
        creditCountByYear: mergeYearAmounts(acc.creditCountByYear, row.creditCountByYear),
        debitCountByYear: mergeYearAmounts(acc.debitCountByYear, row.debitCountByYear),
      }),
      blankSide()
    );
    const investmentFinance = investmentFinanceCategories.reduce(
      (acc, row) => ({
        ...acc,
        value: acc.value + row.debit + row.credit,
        count: acc.count + row.count,
        byYear: mergeYearAmounts(acc.byYear, row.byYear),
        countByYear: mergeYearAmounts(acc.countByYear, row.countByYear),
      }),
      blankSide()
    );
    const mortgageLump = mortgageLumpCategories.reduce(
      (acc, row) => ({
        ...acc,
        value: acc.value + row.credit - row.debit,
        count: acc.count + row.count,
        byYear: mergeYearAmounts(acc.byYear, row.byYear),
        countByYear: mergeYearAmounts(acc.countByYear, row.countByYear),
        credit: acc.credit + row.credit,
        debit: acc.debit + row.debit,
        creditCount: acc.creditCount + yearMapCount(row.creditCountByYear),
        debitCount: acc.debitCount + yearMapCount(row.debitCountByYear),
        creditByYear: mergeYearAmounts(acc.creditByYear, row.creditByYear),
        debitByYear: mergeYearAmounts(acc.debitByYear, row.debitByYear),
        creditCountByYear: mergeYearAmounts(acc.creditCountByYear, row.creditCountByYear),
        debitCountByYear: mergeYearAmounts(acc.debitCountByYear, row.debitCountByYear),
      }),
      blankSide()
    );
    const unknown = unknownCategories.reduce(
      (acc, row) => ({
        ...acc,
        value: acc.value + row.debit + row.credit,
        count: acc.count + row.count,
        byYear: mergeYearAmounts(acc.byYear, row.byYear),
        countByYear: mergeYearAmounts(acc.countByYear, row.countByYear),
      }),
      blankSide()
    );
    const rows: ChartRow[] = [
      {
        name: CHART_INCOME,
        value: income.value,
        count: income.count,
        level: 'side',
        byYear: income.byYear,
        countByYear: income.countByYear,
      },
      {
        name: CHART_EXPENSES,
        value: expenses.value,
        count: expenses.count,
        level: 'side',
        byYear: expenses.byYear,
        countByYear: expenses.countByYear,
      },
      {
        name: CHART_TRANSFERS,
        value: transfers.value,
        count: transfers.count,
        level: 'side',
        byYear: transfers.byYear,
        countByYear: transfers.countByYear,
      },
      {
        name: CHART_INVESTMENTS_PROPERTY,
        value: investmentProperty.value,
        count: investmentProperty.count,
        level: 'side',
        byYear: investmentProperty.byYear,
        countByYear: investmentProperty.countByYear,
        incomeExpenseSplit: true,
        credit: investmentProperty.credit,
        debit: investmentProperty.debit,
        creditCount: investmentProperty.creditCount,
        debitCount: investmentProperty.debitCount,
        creditByYear: investmentProperty.creditByYear,
        debitByYear: investmentProperty.debitByYear,
        creditCountByYear: investmentProperty.creditCountByYear,
        debitCountByYear: investmentProperty.debitCountByYear,
      },
      {
        name: CHART_INVESTMENTS_FINANCE,
        value: investmentFinance.value,
        count: investmentFinance.count,
        level: 'side',
        byYear: investmentFinance.byYear,
        countByYear: investmentFinance.countByYear,
      },
      {
        name: CHART_MORTGAGE_LUMP,
        value: mortgageLump.value,
        count: mortgageLump.count,
        level: 'side',
        byYear: mortgageLump.byYear,
        countByYear: mortgageLump.countByYear,
        incomeExpenseSplit: true,
        credit: mortgageLump.credit,
        debit: mortgageLump.debit,
        creditCount: mortgageLump.creditCount,
        debitCount: mortgageLump.debitCount,
        creditByYear: mortgageLump.creditByYear,
        debitByYear: mortgageLump.debitByYear,
        creditCountByYear: mortgageLump.creditCountByYear,
        debitCountByYear: mortgageLump.debitCountByYear,
      },
      {
        name: CHART_UNKNOWN,
        value: unknown.value,
        count: unknown.count,
        level: 'side',
        byYear: unknown.byYear,
        countByYear: unknown.countByYear,
      },
    ];
    return rows;
  }, [expenseCategories, incomeCategories, investmentFinanceCategories, investmentPropertyCategories, mortgageLumpCategories, transferCategories, unknownCategories]);
  const chartRows = useMemo<ChartRow[]>(() => {
    if (!chartSide) return stats ? sideTotals : [];
    if (chartSide === CHART_INCOME) {
      return incomeCategories
        .map((row) => ({
          name: row.category,
          value: row.credit,
          count: row.count,
          level: 'category' as const,
          byYear: row.byYear,
          countByYear: row.countByYear,
        }))
        .sort((a, b) => b.value - a.value);
    }
    if (chartSide === CHART_TRANSFERS) {
      return transferCategories
        .map((row) => ({
          name: row.category,
          value: row.debit + row.credit,
          count: row.count,
          level: 'category' as const,
          byYear: row.byYear,
          countByYear: row.countByYear,
        }))
        .sort((a, b) => b.value - a.value);
    }
    if (chartSide === CHART_INVESTMENTS_PROPERTY) {
      return groupInvestmentPropertyChartRows(
        investmentPropertyCategories.map((row) => ({
          category: row.category,
          debit: row.debit,
          credit: row.credit,
          count: row.count,
          byYear: row.byYear,
          countByYear: row.countByYear,
        }))
      ).map((row) => ({
        name: row.name,
        value: row.value,
        count: row.count,
        level: 'category' as const,
        byYear: row.byYear,
        countByYear: row.countByYear,
      }));
    }
    if (chartSide === CHART_INVESTMENTS_FINANCE) {
      return investmentFinanceCategories
        .map((row) => ({
          name: row.category,
          value: row.debit + row.credit,
          count: row.count,
          level: 'category' as const,
          byYear: row.byYear,
          countByYear: row.countByYear,
        }))
        .sort((a, b) => b.value - a.value);
    }
    if (chartSide === CHART_MORTGAGE_LUMP) {
      return mortgageLumpCategories
        .map((row) => ({
          name: row.category,
          value: row.credit - row.debit,
          count: row.count,
          level: 'category' as const,
          byYear: row.byYear,
          countByYear: row.countByYear,
          incomeExpenseSplit: true,
          credit: row.credit,
          debit: row.debit,
          creditCount: yearMapCount(row.creditCountByYear),
          debitCount: yearMapCount(row.debitCountByYear),
          creditByYear: row.creditByYear,
          debitByYear: row.debitByYear,
          creditCountByYear: row.creditCountByYear,
          debitCountByYear: row.debitCountByYear,
        }))
        .sort((a, b) => b.value - a.value);
    }
    if (chartSide === CHART_UNKNOWN) {
      const unknown = sideTotals.find((row) => row.name === CHART_UNKNOWN);
      return [
        {
          name: CHART_UNKNOWN,
          value: unknown?.value || 0,
          count: unknown?.count || 0,
          level: 'category' as const,
          byYear: unknown?.byYear || {},
          countByYear: unknown?.countByYear || {},
        },
      ];
    }
    if (!chartGroup) return groupTotals;
    return expenseCategories
      .filter((row) => expenseGroupOf(row.category) === chartGroup)
      .map((row) => ({
        name: row.category,
        value: row.debit - row.credit,
        count: row.count,
        level: 'category' as const,
        byYear: row.byYear,
        countByYear: row.countByYear,
      }))
      .sort((a, b) => b.value - a.value);
  }, [
    chartGroup,
    chartSide,
    expenseCategories,
    groupTotals,
    incomeCategories,
    investmentFinanceCategories,
    investmentPropertyCategories,
    mortgageLumpCategories,
    sideTotals,
    transferCategories,
  ]);
  const chartYearStackKeys = useMemo(
    () => chartYearKeys(allRows.map((row) => row.posted_date)),
    [allRows]
  );
  const stackChartByYear =
    !year && chartYearStackKeys.length > 0 && !isNettingCashflow(chartSide);
  const chartPlotData = useMemo(
    () =>
      stackChartByYear
        ? chartRows.map((row) => flattenYearStack(row, chartYearStackKeys))
        : chartRows,
    [chartRows, chartYearStackKeys, stackChartByYear]
  );
  const chartYearAmountTotals = useMemo(
    () => chartYearTotals(chartRows, chartYearStackKeys),
    [chartRows, chartYearStackKeys]
  );
  const chartXAxis = useMemo(
    () => householdXAxisFromValues(chartRows.map((row) => row.value)),
    [chartRows]
  );
  const chartTrail = useMemo(() => {
    if (!chartSide) return [];
    const sideRow = sideTotals.find((row) => row.name === chartSide);
    const sideAmount = sideRow?.value || 0;
    const sideCount = sideRow?.count || 0;
    if (chartSide === CHART_UNKNOWN) {
      return [
        {
          key: 'side' as const,
          label: CHART_UNKNOWN,
          amount: sideAmount,
          count: sideCount,
          current: true,
        },
      ];
    }
    const showGroup =
      chartSide === CHART_EXPENSES && Boolean(chartGroup) && chartGroup !== chartSide;
    const showCategory = Boolean(category) && category !== chartSide;
    const trail: Array<{
      key: 'side' | 'group' | 'category';
      label: string;
      amount?: number;
      count?: number;
      current: boolean;
    }> = [
      {
        key: 'side',
        label: chartSide,
        amount: sideAmount,
        count: sideCount,
        current: !showGroup && !showCategory,
      },
    ];
    if (showGroup) {
      const groupRow = groupTotals.find((row) => row.name === chartGroup);
      trail.push({
        key: 'group',
        label: chartGroup,
        amount: groupRow?.value || 0,
        count: groupRow?.count || 0,
        current: !showCategory,
      });
    }
    if (showCategory) {
      let amount = 0;
      let count = 0;
      if (chartSide === CHART_INCOME) {
        const row = incomeCategories.find((item) => item.category === category);
        amount = row?.credit || 0;
        count = row?.count || 0;
      } else if (chartSide === CHART_TRANSFERS) {
        const row = transferCategories.find((item) => item.category === category);
        amount = (row?.debit || 0) + (row?.credit || 0);
        count = row?.count || 0;
      } else if (chartSide === CHART_INVESTMENTS_PROPERTY) {
        const row = chartRows.find((item) => item.name === category);
        amount = Number(row?.value || 0);
        count = Number(row?.count || 0);
      } else if (chartSide === CHART_MORTGAGE_LUMP) {
        const row = mortgageLumpCategories.find((item) => item.category === category);
        amount = (row?.credit || 0) - (row?.debit || 0);
        count = row?.count || 0;
      } else if (isInvestmentCashflow(chartSide)) {
        const rows =
          chartSide === CHART_INVESTMENTS_FINANCE
            ? investmentFinanceCategories
            : investmentPropertyCategories;
        const row = rows.find((item) => item.category === category);
        amount = (row?.debit || 0) + (row?.credit || 0);
        count = row?.count || 0;
      } else {
        const row = expenseCategories.find((item) => item.category === category);
        amount = (row?.debit || 0) - (row?.credit || 0);
        count = row?.count || 0;
      }
      trail.push({
        key: 'category',
        label: category,
        amount,
        count,
        current: true,
      });
    }
    return trail;
  }, [
    category,
    chartGroup,
    chartSide,
    expenseCategories,
    groupTotals,
    incomeCategories,
    investmentFinanceCategories,
    investmentPropertyCategories,
    mortgageLumpCategories,
    sideTotals,
    transferCategories,
  ]);
  const kindTotals = stats?.kind_totals || {};
  const allocationTotals = {
    personal: kindTotals.personal || { count: 0, debit: 0, credit: 0 },
    business: {
      count: (kindTotals.business?.count || 0) + (kindTotals.possible_stl_business?.count || 0),
      debit: (kindTotals.business?.debit || 0) + (kindTotals.possible_stl_business?.debit || 0),
      credit: (kindTotals.business?.credit || 0) + (kindTotals.possible_stl_business?.credit || 0),
    },
    'n/a': {
      count: (kindTotals.income?.count || 0) + (kindTotals.transfer?.count || 0),
      debit: (kindTotals.income?.debit || 0) + (kindTotals.transfer?.debit || 0),
      credit: (kindTotals.income?.credit || 0) + (kindTotals.transfer?.credit || 0),
    },
  };
  const accountTotals = stats?.account_totals || [];
  const yearOptions = useMemo(() => {
    const values = new Set<number>();
    (stats?.years || []).forEach((value) => {
      const parsed = Number(value);
      if (parsed) values.add(parsed);
    });
    values.add(Number(CURRENT_YEAR));
    return Array.from(values).sort((a, b) => b - a);
  }, [stats]);

  const drillToSide = (name: string) => {
    setChartSide(name);
    setChartGroup('');
    setCategory('');
    setSelectedChartNames([]);
    setChartOpen(true);
    setPage(1);
  };

  const drillToGroup = (name: string) => {
    setChartSide(CHART_EXPENSES);
    setChartGroup(name);
    setChartOpen(true);
    setCategory('');
    setSelectedChartNames([]);
    setPage(1);
  };

  const drillToCategory = (name: string) => {
    let nextSide = chartSide;
    if (isIncomeCategory(name)) {
      nextSide = CHART_INCOME;
    } else if (isTransferCategory(name)) {
      nextSide = CHART_TRANSFERS;
    } else if (isInvestmentCategory(name)) {
      nextSide = cashflowOfCategory(name, CATEGORY_GROUPS);
    } else if (isUnknownCategory(name) || !expenseGroupOf(name)) {
      if (chartSide !== CHART_INCOME && chartSide !== CHART_EXPENSES) {
        nextSide = CHART_UNKNOWN;
      }
    } else {
      nextSide = CHART_EXPENSES;
    }
    const nextCat = displayCategory(name);
    setChartSide(nextSide);
    setChartGroup(householdUrlGroup('', nextCat, CATEGORY_GROUPS, nextSide));
    setCategory(nextCat);
    setSelectedChartNames([nextCat]);
    setPage(1);
    setChartOpen(true);
  };

  const handleChartNameClick = (name: string, event?: { metaKey?: boolean; ctrlKey?: boolean }) => {
    if (!name) return;
    if (isHouseholdMultiSelectEvent(event)) {
      const next = toggleHouseholdChartSelection(selectedChartNames, name);
      setSelectedChartNames(next);
      setCategory(
        householdCategoryFromChartSelection({
          side: chartSide,
          group: chartGroup,
          selected: next,
        })
      );
      setPage(1);
      setChartOpen(true);
      return;
    }
    setSelectedChartNames([]);
    if (!chartSide) {
      drillToSide(name);
      return;
    }
    if (chartSide === CHART_INVESTMENTS_PROPERTY) {
      setCategory(name);
      setSelectedChartNames([name]);
      setPage(1);
      setChartOpen(true);
      return;
    }
    if (
      chartSide === CHART_INCOME ||
      chartSide === CHART_TRANSFERS ||
      isInvestmentCashflow(chartSide) ||
      chartSide === CHART_MORTGAGE_LUMP ||
      chartSide === CHART_UNKNOWN ||
      Boolean(chartGroup)
    ) {
      drillToCategory(name);
      return;
    }
    drillToGroup(name);
  };

  const clearCategoryFilter = () => {
    setCategory('');
    setSelectedChartNames([]);
    setPage(1);
  };

  const chartBackParent = selectedChartNames.length
    ? chartGroup || chartSide || 'Income vs expenses'
    : category
      ? chartGroup || chartSide
      : chartGroup
        ? chartSide
        : 'Income vs expenses';

  const chartBack = () => {
    if (selectedChartNames.length || category) {
      setCategory('');
      setSelectedChartNames([]);
      if (chartSide !== CHART_EXPENSES) setChartGroup('');
      setPage(1);
      return;
    }
    if (chartGroup) {
      setChartGroup('');
      setSelectedChartNames([]);
      setPage(1);
      return;
    }
    setChartSide('');
    setSelectedChartNames([]);
    setPage(1);
  };

  const clearTableSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const tableSearchField = (
    <TextField
      size="small"
      placeholder="Search ID, description or account"
      value={searchInput}
      onChange={(event) => {
        setSearchInput(event.target.value);
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: searchInput ? (
          <InputAdornment position="end">
            <IconButton aria-label="Clear search" edge="end" size="small" onClick={clearTableSearch}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
      sx={{ minWidth: 220, width: 280 }}
      data-testid="seco-table-search"
    />
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 0.5 }}
        flexWrap="wrap"
        useFlexGap
      >
        <Typography variant="h4" sx={{ mb: 0 }}>
          Personal Finances
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCsv}
            disabled={
              tab === 'omhe'
                ? omheExportRows.length === 0
                : loading || filteredRows.length === 0
            }
            data-testid="seco-export-csv"
          >
            Export
          </Button>
          {canImport && (
            <Button
              variant="contained"
              startIcon={importing ? <CircularProgress size={16} /> : <UploadIcon />}
              onClick={handleImport}
              disabled={importing}
              data-testid="seco-import"
            >
              Import
            </Button>
          )}
        </Stack>
      </Stack>

      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
        {allowedTabs.includes('seco') ? <Tab label="SeCo" value="seco" /> : null}
        {allowedTabs.includes('omhe') ? <Tab label="OmHe" value="omhe" /> : null}
      </Tabs>

      {error && !isToastOnlyApiErrorMessage(error) ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      )}

      {tab === 'omhe' ? (
        <PersonalFinancesOmhe
          year={year}
          onYearChange={(next) => {
            setPage(1);
            setYear(next);
          }}
          onRowsChange={handleOmheRowsChange}
          reloadToken={omheReloadToken}
        />
      ) : (
        <>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mb: 2 }}
            alignItems={{ md: 'flex-end' }}
            flexWrap="wrap"
            useFlexGap
            data-testid="seco-table-filters"
          >
            <HouseholdFilterField label="Year" minWidth={140} testId="seco-year-filter">
              <Select
                value={year}
                displayEmpty
                renderValue={(selected) => (selected ? String(selected) : 'All years')}
                onChange={(event) => {
                  setPage(1);
                  setYear(String(event.target.value));
                }}
              >
                <MenuItem value="">All years</MenuItem>
                {yearOptions.map((value) => (
                  <MenuItem key={value} value={String(value)}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </HouseholdFilterField>
            <HouseholdFilterField label="Account" minWidth={220} testId="seco-account-filter">
              <Select
                value={accountEnding}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return 'All accounts';
                  const row = accountTotals.find((item) => item.ending === selected);
                  return row ? accountChip(row.account, row.ending) : String(selected);
                }}
                onChange={(event) => {
                  setPage(1);
                  setAccountEnding(String(event.target.value));
                  setCategory('');
                  setSelectedChartNames([]);
                  setChartGroup('');
                  setChartSide('');
                }}
              >
                <MenuItem value="">All accounts</MenuItem>
                {accountTotals.map((row) => (
                  <MenuItem key={row.account} value={row.ending}>
                    {optionLabel(accountChip(row.account, row.ending), row.debit, row.credit, row.count)}
                  </MenuItem>
                ))}
              </Select>
            </HouseholdFilterField>
            <HouseholdFilterField label="Type" minWidth={220} testId="seco-type-filter">
              <Select
                value={transactionType}
                displayEmpty
                renderValue={(selected) => (selected ? String(selected) : 'All types')}
                onChange={(event) => {
                  setPage(1);
                  setTransactionType(event.target.value);
                }}
              >
                <MenuItem value="">All types</MenuItem>
                {(stats?.type_totals && stats.type_totals.length > 0
                  ? stats.type_totals
                  : (stats?.transaction_types || []).map((name) => ({
                      transaction_type: name,
                      count: 0,
                      debit: 0,
                      credit: 0,
                    }))
                ).map((row) => (
                  <MenuItem key={row.transaction_type} value={row.transaction_type}>
                    {optionLabel(row.transaction_type, row.debit, row.credit, row.count)}
                  </MenuItem>
                ))}
              </Select>
            </HouseholdFilterField>
            <HouseholdFilterField label="Cashflow" minWidth={200} testId="seco-cashflow-filter">
              <Select
                value={chartSide}
                displayEmpty
                renderValue={(selected) => (selected ? String(selected) : 'All cashflows')}
                onChange={(event) => {
                  setPage(1);
                  setChartSide(String(event.target.value));
                  setChartGroup('');
                  setCategory('');
                  setSelectedChartNames([]);
                }}
              >
                <MenuItem value="">All cashflows</MenuItem>
                {CASHFLOW_FILTER_OPTIONS.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </HouseholdFilterField>
            <HouseholdFilterField label="Category" minWidth={240} testId="seco-category-filter">
              <Select
                value={category}
                displayEmpty
                renderValue={(selected) => (selected ? String(selected) : 'All categories')}
                onChange={(event) => {
                  setPage(1);
                  setCategory(event.target.value);
                  setSelectedChartNames(event.target.value ? [String(event.target.value)] : []);
                  if (event.target.value) {
                    const next = String(event.target.value);
                    let nextSide = chartSide;
                    if (isIncomeCategory(next)) {
                      nextSide = CHART_INCOME;
                    } else if (isTransferCategory(next)) {
                      nextSide = CHART_TRANSFERS;
                    } else if (isInvestmentCategory(next)) {
                      nextSide = cashflowOfCategory(next, CATEGORY_GROUPS);
                    } else if (isUnknownCategory(next) || !expenseGroupOf(next)) {
                      if (chartSide !== CHART_INCOME && chartSide !== CHART_EXPENSES) {
                        nextSide = CHART_UNKNOWN;
                      }
                    } else {
                      nextSide = CHART_EXPENSES;
                    }
                    setChartSide(nextSide);
                    setChartGroup(householdUrlGroup('', next, CATEGORY_GROUPS, nextSide));
                  } else if (chartSide !== CHART_EXPENSES) {
                    setChartGroup('');
                  }
                }}
              >
                <MenuItem value="">All categories</MenuItem>
                {(stats?.category_totals || []).map((row) => (
                  <MenuItem key={row.category} value={row.category}>
                    {optionLabel(row.category, row.debit, row.credit, row.count)}
                  </MenuItem>
                ))}
              </Select>
            </HouseholdFilterField>
            <HouseholdFilterField label="Allocation" minWidth={220} testId="seco-allocation-filter">
              <Select
                value={expenseKind}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return 'All allocations';
                  return ALLOCATION_OPTIONS.find((option) => option.value === selected)?.label || String(selected);
                }}
                onChange={(event) => {
                  setPage(1);
                  setExpenseKind(event.target.value);
                }}
              >
                <MenuItem value="">All allocations</MenuItem>
                {ALLOCATION_OPTIONS.map((option) => {
                  const totals = allocationTotals[option.value as 'personal' | 'business' | 'n/a'];
                  return (
                    <MenuItem key={option.value} value={option.value}>
                      {optionLabel(option.label, totals?.debit, totals?.credit, totals?.count)}
                    </MenuItem>
                  );
                })}
              </Select>
            </HouseholdFilterField>
          </Stack>
          {chartSide || selectedChartNames.length ? (
            <Box sx={{ mb: 2 }}>
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={chartBack}
                data-testid="seco-chart-back"
              >
                {`Back to ${chartBackParent}`}
              </Button>
            </Box>
          ) : null}

          {(year ||
            accountEnding ||
            transactionType ||
            chartSide ||
            category ||
            selectedChartNames.length > 0 ||
            expenseKind ||
            chartGroup) && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 2 }}
              flexWrap="wrap"
              useFlexGap
              data-testid="seco-active-filters"
            >
              {year ? (
                <HouseholdFilterChip
                  size="small"
                  variant="outlined"
                  label={`Year: ${year}`}
                  onDelete={() => {
                    setPage(1);
                    setYear('');
                  }}
                  data-testid="seco-year-filter-chip"
                />
              ) : null}
              {accountEnding ? (
                <HouseholdFilterChip
                  size="small"
                  variant="outlined"
                  label={`Account: ${accountChip(
                    accountTotals.find((row) => row.ending === accountEnding)?.account,
                    accountEnding
                  )}`}
                  onDelete={() => {
                    setPage(1);
                    setAccountEnding('');
                    setCategory('');
                    setChartGroup('');
                    setChartSide('');
                  }}
                  data-testid="seco-account-filter-chip"
                />
              ) : null}
              {transactionType ? (
                <HouseholdFilterChip
                  size="small"
                  variant="outlined"
                  label={`Type: ${transactionType}`}
                  onDelete={() => {
                    setPage(1);
                    setTransactionType('');
                  }}
                  data-testid="seco-type-filter-chip"
                />
              ) : null}
              {chartSide ? (
                <HouseholdFilterChip
                  size="small"
                  variant="outlined"
                  label={`Cashflow: ${chartSide}`}
                  onDelete={() => {
                    setPage(1);
                    setChartSide('');
                    setChartGroup('');
                    setCategory('');
                    setSelectedChartNames([]);
                  }}
                  data-testid="seco-cashflow-filter-chip"
                />
              ) : null}
              {!chartSide
                ? selectedChartNames.map((name) => (
                    <HouseholdFilterChip
                      key={`cashflow-${name}`}
                      size="small"
                      color="primary"
                      label={`Cashflow: ${name}`}
                      onDelete={() => {
                        setSelectedChartNames((prev) => prev.filter((item) => item !== name));
                        setPage(1);
                      }}
                    />
                  ))
                : null}
              {chartGroup && chartSide === CHART_EXPENSES && !category && selectedChartNames.length === 0 ? (
                <HouseholdFilterChip
                  size="small"
                  variant="outlined"
                  label={`Group: ${chartGroup}`}
                  onDelete={chartBack}
                />
              ) : null}
              {chartSide === CHART_EXPENSES && !chartGroup
                ? selectedChartNames.map((name) => (
                    <HouseholdFilterChip
                      key={`group-${name}`}
                      size="small"
                      color="primary"
                      label={`Group: ${name}`}
                      onDelete={() => {
                        setSelectedChartNames((prev) => prev.filter((item) => item !== name));
                        setPage(1);
                      }}
                    />
                  ))
                : null}
              {(chartGroup || (chartSide && chartSide !== CHART_EXPENSES)) && selectedChartNames.length
                ? selectedChartNames.map((name) => (
                    <HouseholdFilterChip
                      key={`category-${name}`}
                      size="small"
                      color="primary"
                      label={
                        isInvestmentPropertyName(name, CATEGORY_GROUPS)
                          ? `Property: ${name}`
                          : `Category: ${name}`
                      }
                      onDelete={() => {
                        setSelectedChartNames((prev) => prev.filter((item) => item !== name));
                        if (category === name) setCategory('');
                        setPage(1);
                      }}
                      data-testid="seco-category-filter-chip"
                    />
                  ))
                : category ? (
                <HouseholdFilterChip
                  size="small"
                  color="primary"
                  label={
                    isInvestmentPropertyName(category, CATEGORY_GROUPS)
                      ? `Property: ${category}`
                      : `Category: ${category}`
                  }
                  onDelete={clearCategoryFilter}
                  data-testid="seco-category-filter-chip"
                />
              ) : null}
              {expenseKind ? (
                <HouseholdFilterChip
                  size="small"
                  variant="outlined"
                  label={`Allocation: ${
                    expenseKind === 'n/a'
                      ? 'N/A'
                      : expenseKind === 'business'
                        ? 'Business'
                        : 'Personal'
                  }`}
                  onDelete={() => {
                    setPage(1);
                    setExpenseKind('');
                  }}
                  data-testid="seco-allocation-filter-chip"
                />
              ) : null}
            </Stack>
          )}

          {(loading || chartRows.length > 0 || Boolean(chartSide)) && (
            <Accordion
              expanded={chartOpen}
              onChange={(_, open) => setChartOpen(open)}
              sx={{ mb: 2 }}
              data-testid="seco-category-chart"
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  {!chartSide
                    ? 'Income vs expenses'
                    : chartSide === CHART_INCOME
                      ? CHART_INCOME
                    : chartSide === CHART_TRANSFERS
                      ? CHART_TRANSFERS
                      : isInvestmentCashflow(chartSide) || chartSide === CHART_MORTGAGE_LUMP
                        ? chartSide
                        : chartSide === CHART_UNKNOWN
                          ? CHART_UNKNOWN
                          : chartGroup
                            ? `Spending: ${chartGroup}`
                            : 'Spending by group'}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {chartOpen && (
                  loading ? (
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', py: 6 }}
                      data-testid="seco-chart-loading"
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                    {stackChartByYear ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mb: 1 }}
                        flexWrap="wrap"
                        useFlexGap
                        data-testid="seco-chart-year-legend"
                      >
                        {chartYearStackKeys.map((stackYear) => (
                          <Chip
                            key={stackYear}
                            size="small"
                            clickable
                            label={stackYear}
                            data-testid={`seco-chart-year-${stackYear}`}
                            onClick={() => {
                              setPage(1);
                              setYear(stackYear);
                            }}
                            sx={{
                              bgcolor: yearLegendFill(stackYear, chartYearAmountTotals),
                              color: yearLegendText(stackYear, chartYearAmountTotals),
                            }}
                          />
                        ))}
                      </Stack>
                    ) : null}
                    <Box sx={{ width: '100%', height: Math.max(chartRows.length <= 2 ? 168 : 280, chartRows.length * HOUSEHOLD_CHART_ROW_HEIGHT), cursor: 'pointer' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartPlotData}
                          layout="vertical"
                          margin={{ top: 8, right: 148, left: 8, bottom: 8 }}
                          style={{ cursor: 'pointer' }}
                          onClick={(state: any, event: any) => {
                            handleChartNameClick(householdChartClickName(state, chartPlotData), event);
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            type="number"
                            tickFormatter={chartAxisEuroTick}
                            domain={chartXAxis.domain}
                            ticks={chartXAxis.ticks}
                            interval={0}
                            allowDecimals={false}
                            tick={{ fontSize: 11 }}
                          />
                          {isNettingCashflow(chartSide) ||
                          (!chartSide && chartRows.some((row) => row.name === CHART_MORTGAGE_LUMP)) ? (
                            <ReferenceLine x={0} stroke="#94A3B8" />
                          ) : null}
                          <YAxis
                            type="category"
                            dataKey="name"
                            width={HOUSEHOLD_CHART_Y_AXIS_WIDTH}
                            interval={0}
                            tick={householdChartYAxisTick(handleChartNameClick, HOUSEHOLD_CHART_Y_AXIS_WIDTH)}
                          />
                          <RechartsTooltip
                            cursor={HOUSEHOLD_CHART_HOVER_CURSOR}
                            content={(props) => (
                              <PersonalFinancesChartTooltip
                                active={props.active}
                                payload={props.payload as any}
                                label={props.label}
                                stackByYear={stackChartByYear}
                                years={chartYearStackKeys}
                                colorFor={(rowName, year) => {
                                  const row = chartRows.find((item) => item.name === rowName);
                                  const base = householdChartRowFill(rowName, chartSide);
                                  return year
                                    ? rowYearShadeFill(base, year, row?.byYear, chartYearStackKeys)
                                    : base;
                                }}
                                formatMoney={(value) => `€${money(value)}`}
                              />
                            )}
                          />
                          {stackChartByYear
                            ? chartYearStackKeys.map((stackYear, yearIndex) => (
                                <Bar
                                  key={stackYear}
                                  dataKey={stackYear}
                                  name={stackYear}
                                  stackId="years"
                                  cursor="pointer"
                                  isAnimationActive={false}
                                  background={yearIndex === 0 ? HOUSEHOLD_CHART_BAR_BACKGROUND : undefined}
                  onClick={(item: any, _index: number, event: any) => {
                    event?.stopPropagation?.();
                    handleChartNameClick(String(item?.payload?.name || ''), event);
                  }}
                                >
                                  {chartRows.map((row) => (
                                    <Cell
                                      key={`${row.name}-${stackYear}`}
                                      fill={rowYearShadeFill(
                                        householdChartRowFill(row.name, chartSide),
                                        stackYear,
                                        row.byYear,
                                        chartYearStackKeys
                                      )}
                                        fillOpacity={householdChartBarOpacity(row.name, selectedChartNames)}
                                    />
                                  ))}
                                  {yearIndex === chartYearStackKeys.length - 1 ? (
                                    <LabelList
                                      dataKey="value"
                                      content={householdBarCountLabel(
                                        chartRows,
                                        (value) => `€${money(value)}`,
                                        'seco-bar-label'
                                      )}
                                    />
                                  ) : null}
                                </Bar>
                              ))
                            : (
                          <Bar
                            dataKey="value"
                            name="Amount"
                            cursor="pointer"
                            isAnimationActive={false}
                            background={HOUSEHOLD_CHART_BAR_BACKGROUND}
                  onClick={(item: any, _index: number, event: any) => {
                    event?.stopPropagation?.();
                    handleChartNameClick(String(item?.payload?.name || ''), event);
                  }}
                          >
                            {chartRows.map((row) => (
                              <Cell
                                key={row.name}
                                fill={householdChartRowFill(row.name, chartSide)}
                                        fillOpacity={householdChartBarOpacity(row.name, selectedChartNames)}
                              />
                            ))}
                            <LabelList
                              dataKey="value"
                              content={householdBarCountLabel(
                                chartRows,
                                (value) => `€${money(value)}`,
                                'seco-bar-label'
                              )}
                            />
                          </Bar>
                            )}
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                    {chartTrail.length > 0 ? (
                      <Breadcrumbs
                        separator=">"
                        sx={{ mt: 1 }}
                        data-testid="seco-chart-breadcrumb"
                      >
                        {chartTrail.map((item) => (
                          <HouseholdFilterChip
                            key={item.key}
                            size="small"
                            color={item.current ? 'primary' : 'default'}
                            variant={item.current ? 'filled' : 'outlined'}
                            label={
                              item.amount != null
                                ? `${item.label} ${householdChartBarLabelText(
                                    item.count || 0,
                                    item.amount,
                                    (value) => `€${money(value)}`
                                  )}`
                                : item.label
                            }
                            onClick={
                              item.current
                                ? undefined
                                : () => {
                                    if (item.key === 'side') {
                                      drillToSide(chartSide);
                                      return;
                                    }
                                    if (item.key === 'group' && chartGroup) {
                                      drillToGroup(chartGroup);
                                    }
                                  }
                            }
                            onDelete={item.current ? chartBack : undefined}
                            data-testid={
                              item.current ? 'seco-category-totals' : undefined
                            }
                          />
                        ))}
                      </Breadcrumbs>
                    ) : null}
                  </>
                  )
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {loading && allRows.length === 0 ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : allRows.length === 0 ? (
            <Alert severity="info">
              No SeCo personal transactions yet.
              {canImport ? ' Import the AIB CSV to load this household’s statement.' : ''}
            </Alert>
          ) : (
            <>
              <SecoPaginationBar
                placement="top"
                total={total}
                perPage={perPage}
                pages={pages}
                page={currentPage}
                onPerPage={(size) => {
                  setPage(1);
                  setPerPage(size);
                }}
                onPage={setPage}
                search={tableSearchField}
              />
              {selectedIds.size > 0 ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 2 }}
                  data-testid="seco-bulk-actions"
                >
                  <Typography variant="body2">{selectedIds.size} selected</Typography>
                  <FormControl size="small" sx={{ minWidth: 220 }} data-testid="seco-bulk-category">
                    <InputLabel>Set category</InputLabel>
                    <Select
                      label="Set category"
                      value={bulkCategory}
                      disabled={bulkSaving}
                      onChange={(event) => setBulkCategory(String(event.target.value))}
                    >
                      {CATEGORY_EDIT_OPTIONS.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!bulkCategory || bulkSaving}
                    onClick={applyBulkCategory}
                    data-testid="seco-bulk-apply"
                  >
                    {bulkSaving ? 'Updating…' : 'Apply'}
                  </Button>
                  <Button
                    size="small"
                    disabled={bulkSaving}
                    onClick={() => {
                      setSelectedIds(new Set());
                      setBulkCategory('');
                    }}
                  >
                    Clear selection
                  </Button>
                </Stack>
              ) : null}
              <TableContainer
                component={Paper}
                data-testid="seco-table-scroll"
                sx={{
                  ...FIT_TABLE_CONTAINER_SX,
                  maxHeight: 'min(70vh, calc(100vh - 220px))',
                  overflowY: 'auto',
                  '& .MuiTableCell-stickyHeader': {
                    zIndex: 3,
                    bgcolor: 'background.paper',
                  },
                }}
              >
                <Table size="small" data-testid="seco-personal-table" stickyHeader sx={FIT_TABLE_SX}>
                  <FitColgroup cols={SECO_TABLE_COLS} />
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" align="center" sx={{ bgcolor: 'background.paper' }}>
                        <Checkbox
                          size="small"
                          checked={allPageSelected}
                          indeterminate={somePageSelected}
                          onChange={toggleSelectAllOnPage}
                          inputProps={{ 'aria-label': 'Select all rows on this page' }}
                          data-testid="seco-select-all"
                        />
                      </TableCell>
                      <SecoSortHeader column="id" label="ID" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <SecoSortHeader column="posted_date" label="Date" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <SecoSortHeader column="cashflow" label="Cashflow" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <SecoSortHeader column="category_group" label="Group" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <SecoSortHeader column="proposed_category" label="Category" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <SecoSortHeader column="allocation" label="Allocation" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <SecoSortHeader column="transaction_type" label="Type" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <SecoSortHeader column="description" label="Description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <SecoSortHeader column="debit_amount" label="Debit" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <SecoSortHeader column="credit_amount" label="Credit" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <SecoSortHeader column="balance" label="Balance" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <SecoSortHeader column="card_last4" label="Card" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <SecoSortHeader column="posted_account" label="Account" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                      <TableCell align="center" sx={{ bgcolor: 'background.paper' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={15}>
                          <Alert severity="info">No transactions match the current filters.</Alert>
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleRows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        selected={selectedIds.has(row.id) || detailRowId === row.id}
                        onClick={() => openDetailRow(row.id)}
                        data-testid={`seco-row-${row.id}`}
                        data-detail-open={detailRowId === row.id ? 'true' : undefined}
                        sx={{
                          cursor: 'pointer',
                          ...(detailRowId === row.id ? openDetailRowSx : {}),
                        }}
                      >
                        <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                          <Checkbox
                            size="small"
                            checked={selectedIds.has(row.id)}
                            onChange={() => toggleRowSelected(row.id)}
                            inputProps={{ 'aria-label': `Select row ${row.id}` }}
                            data-testid={`seco-row-select-${row.id}`}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          <FitCellText title={String(row.id)}>{row.id}</FitCellText>
                        </TableCell>
                        <TableCell data-testid={`seco-date-${row.id}`}>
                          <FitCellText title={formatSecoWhen(row)}>
                            {formatDayMonthYear(row.posted_date)}
                          </FitCellText>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={secoCashflowOf(row, secoPairedIds)} {...fitTableTooltipProps}>
                            <Chip
                              size="small"
                              data-testid={`seco-cashflow-${row.id}`}
                              sx={{
                                ...fitChipSx,
                                ...cashflowChipSx(secoCashflowOf(row, secoPairedIds)),
                              }}
                              label={displayCashflowLabel(secoCashflowOf(row, secoPairedIds))}
                              color={cashflowColor(secoCashflowOf(row, secoPairedIds))}
                              variant="filled"
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell data-testid={`seco-group-${row.id}`}>
                          <FitCellText title={secoExpenseGroupOf(row, secoPairedIds) || '—'}>
                            {secoExpenseGroupOf(row, secoPairedIds) || '—'}
                          </FitCellText>
                        </TableCell>
                        <TableCell
                          onClick={
                            editingId === row.id ? (event) => event.stopPropagation() : undefined
                          }
                        >
                          {editingId === row.id ? (
                            <FormControl size="small" fullWidth data-testid="seco-category-editor">
                              <Select
                                autoFocus
                                value={displayCategory(row.proposed_category)}
                                disabled={savingId === row.id}
                                onChange={(event) => saveCategory(row.id, String(event.target.value))}
                              >
                                {CATEGORY_EDIT_OPTIONS.map((name) => (
                                  <MenuItem key={name} value={name}>
                                    {name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                              sx={{ minWidth: 0, maxWidth: '100%' }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <FitCellText title={row.category_rationale || displayCategory(row.proposed_category)}>
                                {displayCategory(row.proposed_category)}
                              </FitCellText>
                              </Box>
                              {offsetChip(row) ? (
                                <Chip
                                  size="small"
                                  sx={{ ...fitChipSx, flexShrink: 0 }}
                                  color={householdOffsetChipColor(offsetChip(row))}
                                  variant={householdOffsetChipVariant(offsetChip(row))}
                                  label={offsetChip(row)}
                                />
                              ) : null}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            sx={fitChipSx}
                            label={allocationOf(row.expense_kind)}
                            color={allocationColor(row.expense_kind)}
                            variant={allocationOf(row.expense_kind) === 'Business' ? 'filled' : 'outlined'}
                            data-testid={`seco-allocation-${row.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <FitCellText title={row.transaction_type || '—'}>
                            {row.transaction_type || '—'}
                          </FitCellText>
                        </TableCell>
                        <TableCell>
                          <FitCellText title={descriptionOf(row) || '—'}>
                            {descriptionOf(row) || '—'}
                          </FitCellText>
                        </TableCell>
                        <TableCell align="right" sx={{ color: row.debit_amount ? 'error.main' : 'inherit' }}>
                          <FitCellText title={money(row.debit_amount)}>{money(row.debit_amount)}</FitCellText>
                        </TableCell>
                        <TableCell align="right" sx={{ color: row.credit_amount ? 'success.main' : 'inherit' }}>
                          <FitCellText title={money(row.credit_amount)}>{money(row.credit_amount)}</FitCellText>
                        </TableCell>
                        <TableCell align="right">
                          <FitCellText title={money(row.balance)}>{money(row.balance)}</FitCellText>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={cardLast4(row.posted_account) || ''} {...fitTableTooltipProps}>
                            <Chip
                              size="small"
                              data-testid={`seco-card-chip-${row.id}`}
                              sx={{ ...fitChipSx, fontWeight: 500 }}
                              label={displayCardLast4(row.posted_account)}
                              variant="outlined"
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={row.posted_account || ''} {...fitTableTooltipProps}>
                            <Chip
                              size="small"
                              data-testid={`seco-account-chip-${row.id}`}
                              sx={{ ...fitChipSx, fontWeight: 500, ...accountTagChipSx(accountChip(row.posted_account)) }}
                              label={accountChip(row.posted_account)}
                              variant="filled"
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Stack direction="row" spacing={0} justifyContent="flex-end">
                            <Tooltip
                              title={
                                detailRowId === row.id
                                  ? 'Details open in right panel'
                                  : 'Open details in right panel'
                              }
                              {...fitTableTooltipProps}
                            >
                              <IconButton
                                size="small"
                                aria-label={`Open details for ${row.id}`}
                                data-testid={`seco-details-${row.id}`}
                                color={detailRowId === row.id ? 'primary' : 'default'}
                                onClick={() => {
                                  setCommentFocus(false);
                                  setDetailRowId((current) => (current === row.id ? null : row.id));
                                }}
                              >
                                <ViewSidebarIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <HouseholdTxCommentButton
                              rowId={row.id}
                              comment={row.user_comment}
                              comments={row.comments}
                              testIdPrefix="seco"
                              onClick={() => openDetailRow(row.id, { focusComment: true })}
                            />
                            <Tooltip title="View similar transactions" {...fitTableTooltipProps}>
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`View similar transactions for ${row.id}`}
                                  data-testid={`seco-similar-${row.id}`}
                                  disabled={!similarSearchNeedle(row.description1)}
                                  onClick={() => setSimilarRowId(row.id)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Edit category" {...fitTableTooltipProps}>
                              <IconButton
                                size="small"
                                aria-label={`Edit category for ${row.id}`}
                                data-testid={`seco-edit-${row.id}`}
                                onClick={() =>
                                  setEditingId((current) => (current === row.id ? null : row.id))
                                }
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <SecoPaginationBar
                placement="bottom"
                total={total}
                perPage={perPage}
                pages={pages}
                page={currentPage}
                onPerPage={(size) => {
                  setPage(1);
                  setPerPage(size);
                }}
                onPage={setPage}
              />
            </>
          )}

          <Drawer
            anchor="right"
            open={Boolean(detailRow)}
            onClose={closeDetailRow}
            hideBackdrop
            ModalProps={{
              keepMounted: false,
              disableScrollLock: true,
              disableEnforceFocus: true,
              disableAutoFocus: true,
              sx: {
                zIndex: (theme) => theme.zIndex.snackbar + 15,
                pointerEvents: 'none',
              },
            }}
            PaperProps={{
              sx: {
                width: { xs: '100%', sm: DETAIL_PANEL_WIDTH_PX },
                maxWidth: '100vw',
                zIndex: 1,
                pointerEvents: 'auto',
                borderLeft: 1,
                borderColor: 'divider',
                boxShadow: 12,
              },
            }}
          >
            {detailRow ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="seco-detail-panel">
                <Box
                  sx={{
                    flexShrink: 0,
                    px: 2,
                    py: 1.5,
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                      <Typography variant="h6" component="div" sx={{ lineHeight: 1.25 }}>
                        TX {detailRow.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {formatSecoWhen(detailRow)} · {accountChip(detailRow.posted_account)} ·{' '}
                        {detailRow.debit_amount
                          ? money(detailRow.debit_amount)
                          : money(detailRow.credit_amount)}
                      </Typography>
                    </Box>
                    <Tooltip title="Close panel">
                      <IconButton
                        aria-label="Close transaction detail panel"
                        edge="end"
                        onClick={closeDetailRow}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
                <Divider />
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  {detailCounterpart ? (
                    <>
                      <SecoOffsetSideCard
                        row={detailRow}
                        role="this"
                        offsetLabel={offsetChip(detailRow)}
                      />
                      <SecoOffsetSideCard
                        row={detailCounterpart}
                        role="other"
                        offsetLabel={offsetChip(detailCounterpart)}
                        onOpen={() => openDetailRow(detailCounterpart.id)}
                      />
                    </>
                  ) : offsetChip(detailRow) ? (
                    <DetailLine label="Offset" value={offsetChip(detailRow)} />
                  ) : null}
                  <DetailLine label="ID" value={detailRow.id} />
                  <DetailLine label="Date" value={formatSecoWhen(detailRow)} />
                  <DetailLine label="Account" value={detailRow.posted_account} />
                  <DetailLine label="Card" value={displayCardLast4(detailRow.posted_account)} />
                  <DetailLine label="Type" value={detailRow.transaction_type} />
                  <DetailLine
                    label="Cashflow"
                    value={secoCashflowOf(detailRow, secoPairedIds)}
                  />
                  <DetailLine
                    label="Group"
                    value={secoExpenseGroupOf(detailRow, secoPairedIds) || '—'}
                  />
                  <DetailLine
                    label="Category"
                    value={displayCategory(detailRow.proposed_category)}
                  />
                  <DetailLine
                    label="Allocation"
                    value={allocationOf(detailRow.expense_kind)}
                  />
                  <DetailLine label="Description 1" value={detailRow.description1} />
                  <DetailLine label="Description 2" value={detailRow.description2} />
                  <DetailLine label="Description 3" value={detailRow.description3} />
                  <DetailLine label="Debit" value={money(detailRow.debit_amount)} />
                  <DetailLine label="Credit" value={money(detailRow.credit_amount)} />
                  <DetailLine label="Balance" value={money(detailRow.balance)} />
                  <DetailLine label="Currency" value={detailRow.posted_currency} />
                  <DetailLine label="Rationale" value={detailRow.category_rationale} />
                  <HouseholdTxCommentThread
                    household="seco"
                    rowId={detailRow.id}
                    comments={detailRow.comments}
                    testIdPrefix="seco"
                    autoFocus={commentFocus}
                    onTransaction={(patch) => applySecoTransaction({ ...detailRow, ...patch })}
                    onError={setError}
                  />
                </Box>
              </Box>
            ) : null}
          </Drawer>
        </>
      )}
      {similarAnchor ? (
        <HouseholdSimilarTxDialog
          open
          needle={similarSearchNeedle(similarAnchor.description1)}
          rows={similarTxViews}
          onClose={() => setSimilarRowId(null)}
          onSelectRow={(id) => {
            setSimilarRowId(null);
            openDetailRow(id);
          }}
        />
      ) : null}
    </Box>
  );
};

export default PersonalFinances;
